import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { InvitationCollectionDeleteItemsPreviewData } from '@app/v2/event/types/invitation-tracker.type'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { ModalService } from '@app/v2/shared/services/modal.service'

const BULK_DELETE_HEADER_ICON = `<svg class="tw-w-3 tw-h-3 lg:tw-w-4 lg:tw-h-4 tw-shrink-0 tw-block" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clip-path="url(#bulk_delete_trash_clip)"><path d="M14.5185 3.36842H11.2593V1.68421C11.2593 1.23753 11.0876 0.809145 10.782 0.493294C10.4763 0.177443 10.0618 0 9.62963 0H6.37037C5.93817 0 5.52366 0.177443 5.21805 0.493294C4.91243 0.809145 4.74074 1.23753 4.74074 1.68421V3.36842H1.48148C1.26538 3.36842 1.05813 3.45714 0.90532 3.61507C0.752513 3.77299 0.666667 3.98719 0.666667 4.21053C0.666667 4.43387 0.752513 4.64806 0.90532 4.80598C1.05813 4.96391 1.26538 5.05263 1.48148 5.05263H2.2963V14.3158C2.2963 14.7625 2.46799 15.1909 2.7736 15.5067C3.07922 15.8226 3.49372 16 3.92593 16H12.0741C12.5063 16 12.9208 15.8226 13.2264 15.5067C13.532 15.1909 13.7037 14.7625 13.7037 14.3158V5.05263H14.5185C14.7346 5.05263 14.9419 4.96391 15.0947 4.80598C15.2475 4.64806 15.3333 4.43387 15.3333 4.21053C15.3333 3.98719 15.2475 3.77299 15.0947 3.61507C14.9419 3.45714 14.7346 3.36842 14.5185 3.36842ZM6.37037 1.68421H9.62963V3.36842H6.37037V1.68421ZM7.18519 12.6316C7.18519 12.8549 7.09934 13.0691 6.94653 13.227C6.79372 13.385 6.58647 13.4737 6.37037 13.4737C6.15427 13.4737 5.94702 13.385 5.79421 13.227C5.6414 13.0691 5.55556 12.8549 5.55556 12.6316V6.73684C5.55556 6.5135 5.6414 6.29931 5.79421 6.14138C5.94702 5.98346 6.15427 5.89474 6.37037 5.89474C6.58647 5.89474 6.79372 5.98346 6.94653 6.14138C7.09934 6.29931 7.18519 6.5135 7.18519 6.73684V12.6316ZM10.4444 12.6316C10.4444 12.8549 10.3586 13.0691 10.2058 13.227C10.053 13.385 9.84573 13.4737 9.62963 13.4737C9.41353 13.4737 9.20628 13.385 9.05347 13.227C8.90066 13.0691 8.81481 12.8549 8.81481 12.6316V6.73684C8.81481 6.5135 8.90066 6.29931 9.05347 6.14138C9.20628 5.98346 9.41353 5.89474 9.62963 5.89474C9.84573 5.89474 10.053 5.98346 10.2058 6.14138C10.3586 6.29931 10.4444 6.5135 10.4444 6.73684V12.6316Z" fill="#111928"/></g><defs><clipPath id="bulk_delete_trash_clip"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

@Component({
  selector: 'bulk-delete-modal',
  standalone: true,
  imports: [ModalComponent, ProgressCircleComponent],
  templateUrl: './bulk-delete-modal.component.html',
  styleUrl: './bulk-delete-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkDeleteModalComponent {
  public readonly id = input.required<string>()
  public readonly personaName = input.required<string>()
  public readonly preview = input<InvitationCollectionDeleteItemsPreviewData | null>(null)
  public readonly isLoading = input(false)

  public readonly confirmed = output<void>()
  public readonly cancelled = output<void>()

  public readonly headerIcon = BULK_DELETE_HEADER_ICON

  public readonly confirmLabel = computed(() => {
    const count = this.preview()?.will_be_removed ?? 0
    const label = count === 1 ? 'entry' : 'entries'
    return `Remove ${count} ${label}`
  })

  public readonly selectedCountLabel = computed(() => {
    const count = this.preview()?.selected_count ?? 0
    return `${count} ${count === 1 ? 'entry' : 'entries'}`
  })

  public readonly willBeRemovedLabel = computed(() => {
    const count = this.preview()?.will_be_removed ?? 0
    if (count === 1) return "1 entry hasn't been invited yet and will be removed."
    return `${count} entries haven't been invited yet and will be removed.`
  })

  public readonly wontBeRemovedLabel = computed(() => {
    const count = this.preview()?.wont_be_removed ?? 0
    if (count === 1) return "1 entry has already been invited and won't be removed."
    return `${count} entries have already been invited and won't be removed.`
  })

  private readonly _modalService = inject(ModalService)

  /**
   * Confirms bulk remove. Modal close is left to the parent after the API succeeds.
   */
  public onConfirm(): void {
    if (this.isLoading() || !this.preview()?.will_be_removed) return
    this.confirmed.emit()
  }

  /**
   * Cancels and closes the modal.
   */
  public onCancel(): void {
    if (this._modalService.isModalOpen(this.id())) {
      this._modalService.toggleModal(this.id())
    }
    this.cancelled.emit()
  }

  /**
   * Handles close from the modal X button or backdrop.
   */
  public onModalClose(): void {
    this.cancelled.emit()
  }
}
